import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeatureSelector } from '@ngrx/store';

import { SortOrder } from '@common/utils/search-sort';
import { AvailableTabsOnPage, LastPageStateWithTabs } from '@store/types/basic-last-page-state';

import { TrailerGroupsItem, TrailerItem } from '../../app/portal/pages/trailers/trailers.types';

/**
 * Селектор для получения состояния модуля `trailers` из общего состояния приложения.
 */
export const selectTrailersState = createFeatureSelector<TrailersState>('trailers');

/**
 * Интерфейс состояния для модуля `trailers`.
 *
 * @property {EntityState<TrailerItem>} list - Список прицепов.
 * @property {EntityState<TrailerGroupsItem>} groupsList - Список групп прицепов.
 * @property {LastPageStateWithTabs} lastPageState - Состояние последней страницы с вкладками.
 * @property {boolean} loading - Флаг загрузки данных.
 * @property {string | undefined} error - Сообщение об ошибке, если есть.
 */
export interface TrailersState {
  /** Список прицепов */
  list: EntityState<TrailerItem>;
  /** Список групп прицепов */
  groupsList: EntityState<TrailerGroupsItem>;
  /** Состояние последней страницы с вкладками */
  lastPageState: LastPageStateWithTabs;
  /** Флаг загрузки данных */
  loading: boolean;
  /** Сообщение об ошибке, если есть */
  error?: string;
}

/**
 * Адаптер для управления состоянием списка прицепов.
 */
export const trailersAdapter: EntityAdapter<TrailerItem> = createEntityAdapter<TrailerItem>({
  selectId: (item) => item.id as number,
});

/**
 * Адаптер для управления состоянием списка групп прицепов.
 */
export const trailerGroupsAdapter: EntityAdapter<TrailerGroupsItem> =
  createEntityAdapter<TrailerGroupsItem>({
    selectId: (item) => item.id as number,
  });

/**
 * Начальное состояние для модуля `trailers`.
 */
export const initialTrailersState: TrailersState = {
  list: trailersAdapter.getInitialState(),
  groupsList: trailerGroupsAdapter.getInitialState(),
  lastPageState: {
    activeTabOnPage: AvailableTabsOnPage.List,
    [AvailableTabsOnPage.List]: {
      searchValue: '',
      sortOrder: SortOrder.Asc,
      loading: false,
    },
    [AvailableTabsOnPage.Groups]: {
      searchValue: '',
      sortOrder: SortOrder.Asc,
      openedGroupId: null,
      isOpenedFreeList: false,
      loading: false,
    },
  },
  loading: false,
  error: undefined,
};
